import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';

const FCM_API_URL = 'https://us-central1-bpnpshuttle.cloudfunctions.net/api';

export const NoticeWritePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const sendPushNotification = async (
    title: string,
    content: string,
    noticeId: string
  ) => {
    try {
      console.log('푸시 알림 발송 시도');

      const response = await fetch(FCM_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: '/topics/all',
          notification: {
            title: `[긴급] ${title}`,
            body: content,
          },
          android: {
            clickAction: 'FLUTTER_NOTIFICATION_CLICK',
            priority: 'high',
          },
          data: {
            screen: 'NoticeDetailScreen',
            noticeId: noticeId,
          },
        }),
      });

      if (response.ok) {
        // Firestore에 알림 기록 저장
        await addDoc(collection(db, 'notifications'), {
          userId: '*',
          title: `[긴급] ${title}`,
          body: content,
          type: 'urgent_notice',
          referenceId: noticeId,
          createdAt: serverTimestamp(),
          isRead: false,
          isPublic: true,
          data: {
            screen: 'notice_detail',
            noticeId: noticeId,
          },
        });
        console.log('푸시 알림 발송 성공');
      } else {
        const errorText = await response.text();
        console.error('푸시 알림 발송 실패:', response.status, errorText);
        throw new Error(`푸시 알림 발송 실패: ${response.status}`);
      }
    } catch (err) {
      console.error('푸시 알림 발송 중 오류:', err);
      throw err;
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!content.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // 공지사항 저장
      const docRef = await addDoc(collection(db, 'notices'), {
        title: title.trim(),
        content: content.trim(),
        isUrgent,
        authorId: user.uid,
        writer: user.uid, // 공지 작성자 UID
        createdAt: serverTimestamp(),
      });

      // 긴급 공지인 경우 푸시 알림 발송
      if (isUrgent) {
        try {
          await sendPushNotification(
            title.trim(),
            content.trim(),
            docRef.id
          );
        } catch (pushError) {
          console.error('푸시 알림 발송 실패:', pushError);
          // 푸시 알림 실패해도 공지사항은 저장되었으므로 경고만 표시
          setError('공지사항은 저장되었지만 푸시 알림 발송에 실패했습니다.');
          setSubmitting(false);
          return;
        }
      }

      navigate('/notices');
    } catch (err: any) {
      console.error('Error creating notice:', err);
      setError('공지사항 작성에 실패했습니다. 다시 시도해주세요.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/notices');
  };

  return (
    <MainLayout>
      <Box>
        {/* 뒤로가기 버튼 */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleCancel}
          sx={{ mb: 3 }}
        >
          목록으로 돌아가기
        </Button>

        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          새 공지사항 작성
        </Typography>

        <Paper sx={{ p: 3, mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* 제목 입력 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              제목
            </Typography>
            <TextField
              fullWidth
              placeholder="공지사항 제목을 입력하세요"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
            />
          </Box>

          {/* 내용 입력 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              내용
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={15}
              placeholder="공지사항 내용을 입력하세요"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={submitting}
            />
          </Box>

          {/* 긴급 공지 체크박스 */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    긴급 공지
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    긴급 공지로 설정하면 목록에서 강조 표시되고, 모든 앱 사용자에게 푸시 알림이 발송됩니다.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* 버튼 그룹 */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={submitting}
              size="large"
            >
              취소
            </Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSubmit}
              disabled={submitting || !title.trim() || !content.trim()}
              size="large"
            >
              등록
            </Button>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};
